import { CheckVerified01, Eye, Folder, UploadCloud01 } from '@ethsign/icons';

import { Badge, Button, Select } from '@ethsign/ui';

import { useQuery } from '@tanstack/react-query';

import { useEffect, useState } from 'react';

import { Empty } from '@/components';
import { FileUploadArea } from '@/components/FileUploadArea';
import { ColorUploadFileIcon } from '@/components/Icons';
import { Spin } from '@/components/Loading';
import { ContractTypeTag, TemplatePreviewModal } from '@/components/TemplatePreview';
import { useRouter } from '@/hooks';
import { cn } from '@/lib/utils';
import { queryContractTemplates, queryTemplateCategories } from '@/services';
import { dateFormat, renderSize, stringifyQueryString } from '@/utils';

type Props = {
  onUpload: (file: File) => void;
  file: File;
  id: string;
  folderId?: string;
};

export const UploadContract = (props: Props) => {
  const { onUpload, id, folderId } = props;
  const [categoryId, setCategoryId] = useState<string | undefined>('all');
  const router = useRouter();
  const [firstLoaded, setFirstLoaded] = useState(false);

  const [file, setFile] = useState<File>();
  useEffect(() => {
    props.file && setFile(props.file);
  }, [props.file]);

  const { data, isLoading } = useQuery({
    queryKey: ['contract-templates', categoryId],
    queryFn: () => queryContractTemplates({ categoryId: categoryId === 'all' ? undefined : categoryId })
  });
  const { data: categoryData } = useQuery({
    queryKey: ['categoryList'],
    queryFn: queryTemplateCategories
  });

  useEffect(() => {
    if (data) {
      setFirstLoaded(true);
    }
  }, [data]);

  return (
    <div className={'pb-5'}>
      <div className="text-[25px]/[34px] text-gray-900 font-semiBold">Upload Contract</div>
      <div className="text-sm font-normal text-gray-60 mt-1 mb-8">Upload a document that you wish to send.</div>
      {file ? (
        <div className="w-full h-[152px] bg-tangerine-25 border border-tangerine-200 rounded-2xl flex flex-row items-center justify-between p-12">
          <div className="flex flex-row items-center">
            <ColorUploadFileIcon />
            <div>
              <div className=" text-lg font-normal text-gray-900">{file.name}</div>
              <div className="mt-2 text-sm text-gray-500 font-normal">
                {dateFormat((file as any).uploadTime, 'datetime')} - {renderSize(file.size)}
              </div>
            </div>
          </div>
          <FileUploadArea
            onUpload={async (file) => {
              onUpload(file[0]);
              setFile(file[0]);
            }}
          >
            <Button className="mr-3" size="lg" variant="secondary-gray">
              <UploadCloud01 size={20} color="#101828" />
              <span className="ml-3">Re-Upload</span>
            </Button>
          </FileUploadArea>
        </div>
      ) : (
        <FileUploadArea
          className={'!h-[284px]'}
          mode="view2"
          onUpload={async (file) => {
            onUpload(file[0]);
            setFile(file[0]);
          }}
        ></FileUploadArea>
      )}

      <div>
        <div className="flex items-center justify-between mt-10 mb-5">
          <div className="text-gray-900 text-lg font-semiBold">Select From Template</div>
          <Select
            className={'min-w-[190px]'}
            value={categoryId}
            onChange={setCategoryId}
            placeholder={'Select Category'}
            options={[
              {
                label: 'All Category',
                value: 'all'
              },
              ...(categoryData?.rows?.map((it: any) => ({
                label: it.categoryName,
                value: it.id
              })) || [])
            ]}
          />
        </div>
        <div className="relative">
          <Spin loading={isLoading}>
            {data?.rows?.length ? (
              <div className="grid grid-cols-4 gap-4">
                {data?.rows?.map((item, index) => {
                  const isCollection = item.type === 'collection';
                  return (
                    <div
                      key={index}
                      className="group cursor-pointer rounded-lg border border-gray-200 overflow-hidden relative"
                    >
                      <div className={'bg-[#E8EDEB] h-[150px] pt-6 relative'}>
                        <img
                          src={
                            isCollection
                              ? 'https://sign-public-cdn.s3.us-east-1.amazonaws.com/signature-next/collectionbg_240723065542.webp'
                              : item.coverPicture
                          }
                          className={cn(
                            'm-auto h-full rounded-t-[10px] object-cover',
                            isCollection ? 'w-[240px]' : 'w-[180px]'
                          )}
                          alt=""
                        />
                        {isCollection ? (
                          <Badge className={'rounded-sm bg-tangerine-50 text-tangerine-500 absolute left-4 bottom-4'}>
                            <span className={'inline-block mr-1.5'}>
                              <Folder color={'#EF6820'} size={20} />
                            </span>
                            {item.templates?.length} Templates
                          </Badge>
                        ) : null}
                      </div>
                      <div className="px-3 py-4">
                        <ContractTypeTag type={item?.categoryName} />
                        <div className="text-md font-medium text-gray-900 mt-3 flex gap-1 items-center">
                          {item.name}
                          {item.status === 'verified' && (
                            <span>
                              <CheckVerified01 size={16} color="#EF6820"></CheckVerified01>
                            </span>
                          )}
                        </div>
                        <div className="mt-4 text-xs">
                          {isCollection ? null : (
                            <>
                              <span className="text-gray-400">Jurisdiction:</span>
                              <span className="text-gray-900"> {item.applicableJurisdictions}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="hidden group-hover:flex justify-center items-center absolute top-0 left-0 bg-[rgba(0,0,0,0.3)] w-full h-full">
                        <TemplatePreviewModal
                          data={item}
                          trigger={
                            <Button variant="outline" className={'w-[180px]'}>
                              <Eye className="mr-2" />
                              Preview
                            </Button>
                          }
                          handleUse={(detail) => {
                            router.replace(
                              `/prepare/${id}?${stringifyQueryString({ templateId: detail?.id, folderId: folderId })}`
                            );
                          }}
                          key={item.templateId}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : firstLoaded && data?.rows?.length === 0 ? (
              <Empty />
            ) : null}
          </Spin>
        </div>
      </div>
    </div>
  );
};
